import { Injectable } from '@angular/core';
import { AuthService } from 'src/app/services/auth.service';
import { HttpService } from 'src/app/services/http.service';
import { Subject, Observable } from 'rxjs';
import { tap, catchError, shareReplay, map } from 'rxjs/operators';
import { HttpResponse } from '@angular/common/http';
import { AppConstants } from 'src/app/constants/app-constants';
import { environment } from 'src/environments/environment';

@Injectable({
    providedIn: 'root',
})
export class MultilingualService {
    private authData: any;
    private multilingualSubject = new Subject();

    multilingual$: Observable<any> = this.multilingualSubject.asObservable();

    constructor(private auth: AuthService, private http: HttpService) {
        this.authData = this.auth.getAuthData();
    }

    getMultilingualData(pageNo: number = 1) {
        const url = `${environment.apiUrlDomain}/multilingual?pageNo=${pageNo}`;

        const headers = {
            'Content-Type': 'application/json',
            'x-access-token': this.authData.token,
        };

        this.http
            .getRequest(url, headers)
            .pipe(
                tap((response: any) => {
                    if (response.status == 200) {
                        this.multilingualSubject.next({
                            type: 'MULTILINGUAL_DATA_SUCCESSFUL',
                            data: response.body,
                        });
                    }
                    console.log(response);
                }),
                catchError((error) => {
                    this.multilingualSubject.next({
                        type: 'MULTILINGUAL_DATA_FAILED',
                        data: error,
                    });
                    throw error;
                }),
                shareReplay()
            )
            .subscribe();
    }

    getSourceTypes() {
        const url = `${environment.apiUrlDomain}/multilingual/sources`;

        const headers = {
            'Content-Type': 'application/json',
            'x-access-token': this.authData.token,
        };

        this.http
            .getRequest(url, headers)
            .pipe(
                tap((response: any) => {
                    if (response.status == 200) {
                        this.multilingualSubject.next({
                            type: 'MULTILINGUAL_SOURCE_TYPE_SUCCESSFUL',
                            data: response.body,
                        });
                    }
                    console.log(response);
                }),
                catchError((error) => {
                    this.multilingualSubject.next({
                        type: 'MULTILINGUAL_SOURCE_TYPE_FAILED',
                        data: error,
                    });
                    throw error;
                }),
                shareReplay()
            )
            .subscribe();
    }

    getLanguageTypes() {
        const url = `${environment.apiUrlDomain}/languages?projection=name%20id&pageSize=200`;

        const headers = {
            'Content-Type': 'application/json',
            'x-access-token': this.authData.token,
        };

        this.http
            .getRequest(url, headers)
            .pipe(
                tap((response: any) => {
                    if (response.status == 200) {
                        this.multilingualSubject.next({
                            type: 'MULTILINGUAL_LANGUAGE_TYPE_SUCCESSFUL',
                            data: response.body,
                        });
                    }
                    console.log(response);
                }),
                catchError((error) => {
                    this.multilingualSubject.next({
                        type: 'MULTILINGUAL_LANGUAGE_TYPE_FAILED',
                        data: error,
                    });
                    throw error;
                }),
                shareReplay()
            )
            .subscribe();
    }

    searchMultilingualData(searchKey: string = '', source: string = '', languageId: string = '', pageNo: number = 1) {
        let url = `${environment.apiUrlDomain}/multilingual`;
        const queryParams: string[] = [];
        if (searchKey) {
            queryParams.push(`refId=${searchKey}`);
        }
        if (source) {
            queryParams.push(`source=${source}`);
        }
        if (languageId) {
            queryParams.push(`languageId=${languageId}`);
        }
        queryParams.push(`pageNo=${pageNo}`);
        if (queryParams.length > 0) {
            url += `?${queryParams.join('&')}`;
        }

        const headers = {
            'Content-Type': 'application/json',
            'x-access-token': this.authData.token,
        };

        this.http
            .getRequest(url, headers)
            .pipe(
                tap((response: any) => {
                    if (response.status == 200) {
                        this.multilingualSubject.next({
                            type: 'MULTILINGUAL_SEARCH_DATA_SUCCESSFUL',
                            data: response.body,
                        });
                    }
                    console.log(response);
                }),
                catchError((error) => {
                    this.multilingualSubject.next({
                        type: 'MULTILINGUAL_SEARCH_DATA_FAILED',
                        data: error,
                    });
                    throw error;
                }),
                shareReplay()
            )
            .subscribe();
    }

    searchMultilingualCreateData(searchKey: string = '', source: string = '', pageNo: any = '') {
        const qParam = ['category', 'screen'].includes(source) ? 'q' : 'any';
        const searchParam = searchKey ? `&${qParam}=${encodeURIComponent(searchKey)}` : '';
        let url = ``;
        switch (source) {
            case 'category':
                url = `${environment.apiUrlDomain}/categories?pageNo=${pageNo}${searchParam}`;
                break;
            case 'metadata':
                url = `${environment.apiUrlDomain}/metadata?pageNo=${pageNo}${searchParam}`;
                break;
            case 'screen':
                url = `${environment.apiUrlDomain}/screens?pageNo=${pageNo}${searchParam}`;
                break;
            case 'channel':
                url = `${environment.apiUrlDomain}/channels?pageNo=${pageNo}${searchParam}`;
                break;
            case 'genre':
                url = `${environment.apiUrlDomain}/genres?pageNo=${pageNo}${searchParam}`;
                break;
            case 'broadcaster':
                url = `${environment.apiUrlDomain}/broadcasters?pageNo=${pageNo}${searchParam}`;
                break;
        }

        if (!url) {
            return;
        }

        const headers = {
            'Content-Type': 'application/json',
            'x-access-token': this.authData.token,
        };

        this.http
            .getRequest(url, headers)
            .pipe(
                tap((response: any) => {
                    if (response.status == 200) {
                        this.multilingualSubject.next({
                            type: 'MULTILINGUAL_SEARCH_CREATE_DATA_SUCCESSFUL',
                            data: response.body,
                        });
                    }
                    console.log(response);
                }),
                catchError((error) => {
                    this.multilingualSubject.next({
                        type: 'MULTILINGUAL_SEARCH_CREATE_DATA_FAILED',
                        data: error,
                    });
                    throw error;
                }),
                shareReplay()
            )
            .subscribe();
    }

    getDataOnAdd(id: any) {
        const url = `${environment.apiUrlDomain}/multilingual/${id}`;

        const headers = {
            'Content-Type': 'application/json',
            'x-access-token': this.authData.token,
        };

        this.http
            .getRequest(url, headers)
            .pipe(
                tap((response: any) => {
                    if (response.status == 200) {
                        this.multilingualSubject.next({
                            type: 'MULTILINGUAL_GET_ADD_DATA_SUCCESSFUL',
                            data: response.body,
                        });
                    }
                    console.log(response);
                }),
                catchError((error) => {
                    this.multilingualSubject.next({
                        type: 'MULTILINGUAL_GET_ADD_DATA_FAILED',
                        data: error,
                    });
                    throw error;
                }),
                shareReplay()
            )
            .subscribe();
    }

    createEditMultilingual(data: any) {
        const url = `${environment.apiUrlDomain}/multilingual`;

        const headers = {
            'Content-Type': 'application/json',
            'x-access-token': this.authData.token,
        };

        this.http
            .postRequest(url, headers, data)
            .pipe(
                tap((response: any) => {
                    if (response.status == 201) {
                        this.multilingualSubject.next({
                            type: 'MULTILINGUAL_CREATE_EDIT_SUCCESSFUL',
                            data: response.body,
                        });
                    }
                    console.log(response);
                }),
                catchError((error) => {
                    this.multilingualSubject.next({
                        type: 'MULTILINGUAL_CREATE_EDIT_FAILED',
                        data: error,
                    });
                    throw error;
                }),
                shareReplay()
            )
            .subscribe();
    }
}
